import React, { useState } from 'react';
import { useAuth } from '../hooks';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Badge } from '../components/ui';
import { User, CreditCard, Bell, Shield } from 'lucide-react';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'plan' | 'notifications' | 'security'>('profile');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <User size={16} className="inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('plan')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'plan'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CreditCard size={16} className="inline mr-2" />
            Plan & Billing
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell size={16} className="inline mr-2" />
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'security'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield size={16} className="inline mr-2" />
            Security
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'profile' && <ProfileTab user={user} />}
        {activeTab === 'plan' && <PlanTab user={user} />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'security' && <SecurityTab />}
      </div>
    </div>
  );
};

// Profile Tab
const ProfileTab: React.FC<{ user: any; refreshUser?: () => void }> = ({ user }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Email Address"
            value={user?.email || ''}
            disabled
            fullWidth
          />
          <div className="flex items-center gap-2">
            <Badge variant={user?.emailVerified ? 'success' : 'warning'}>
              {user?.emailVerified ? 'Email Verified' : 'Email Not Verified'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API Key</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Use this API key to authenticate requests to the MCP Hub API.
          </p>
          <Input
            label="API Key"
            value="••••••••••••••••••••"
            disabled
            fullWidth
            helperText="Click to reveal your API key"
          />
          <Button variant="secondary" size="sm">
            Regenerate API Key
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Plan Tab
const PlanTab: React.FC<{ user: any }> = ({ user }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900 uppercase">
                {user?.plan} Plan
              </p>
              {user?.plan === 'free' && user?.trialEndsAt && !user?.trialExpired && (
                <p className="text-sm text-gray-600 mt-1">
                  Trial ends on {new Date(user.trialEndsAt).toLocaleDateString()}
                </p>
              )}
              {user?.trialExpired && (
                <p className="text-sm text-red-600 mt-1">
                  Trial expired
                </p>
              )}
            </div>
            {user?.plan === 'free' && (
              <Button variant="primary">
                Upgrade to Pro
              </Button>
            )}
          </div>

          {user?.plan === 'free' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-4">
              <p className="text-sm text-blue-800 font-medium mb-2">
                Upgrade to Pro for:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                <li>Unlimited clients and servers</li>
                <li>Unlimited queries and executions</li>
                <li>Priority support</li>
                <li>Advanced analytics</li>
                <li>Custom integrations</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usage Statistics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-700">Total Clients</span>
            <span className="font-semibold">{user?.totalClients || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-700">Total Servers</span>
            <span className="font-semibold">{user?.totalServers || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-700">Total Queries</span>
            <span className="font-semibold">{user?.totalQueries || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-700">Total Executions</span>
            <span className="font-semibold">{user?.totalExecutions || 0}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Notifications Tab
const NotificationsTab: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between py-3 border-b">
          <div>
            <p className="font-medium text-gray-900">Email Notifications</p>
            <p className="text-sm text-gray-600">Receive email updates about your account</p>
          </div>
          <input type="checkbox" className="w-4 h-4" defaultChecked />
        </div>
        <div className="flex items-center justify-between py-3 border-b">
          <div>
            <p className="font-medium text-gray-900">Server Status Alerts</p>
            <p className="text-sm text-gray-600">Get notified when servers disconnect</p>
          </div>
          <input type="checkbox" className="w-4 h-4" defaultChecked />
        </div>
        <div className="flex items-center justify-between py-3">
          <div>
            <p className="font-medium text-gray-900">Usage Alerts</p>
            <p className="text-sm text-gray-600">Alerts when approaching usage limits</p>
          </div>
          <input type="checkbox" className="w-4 h-4" defaultChecked />
        </div>
        <div className="pt-4">
          <Button variant="primary">Save Preferences</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Security Tab
const SecurityTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            fullWidth
            placeholder="••••••••"
          />
          <Input
            label="New Password"
            type="password"
            fullWidth
            placeholder="••••••••"
          />
          <Input
            label="Confirm New Password"
            type="password"
            fullWidth
            placeholder="••••••••"
          />
          <Button variant="primary">Update Password</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Add an extra layer of security to your account by enabling two-factor authentication.
          </p>
          <Badge variant="warning">Not Enabled</Badge>
          <div className="pt-2">
            <Button variant="secondary">Enable 2FA</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
